import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { itemsPerPageOptions } from "../table/Table.types";

interface CardGridPaginationProps {
  totalItems: number;
}

export const CardGridPagination = ({ totalItems }: CardGridPaginationProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const limit = +(queryParams.get("limit") || 21);
  const skip = +(queryParams.get("skip") || 0);
  const currentPage = Math.floor(skip / limit) + 1;
  const numberOfPages = Math.ceil(totalItems / limit) || 1;

  const goToPage = (page: number) => {
    const newSkip = (page - 1) * limit;
    queryParams.set("skip", newSkip.toString());
    queryParams.set("limit", limit.toString());
    navigate({ search: queryParams.toString() });
  };

  const onItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = parseInt(e.target.value);
    queryParams.set("limit", newLimit.toString());
    queryParams.delete("skip");
    navigate({ search: queryParams.toString() });
  };

  if (totalItems === 0) return null;

  const rangeStart = skip + 1;
  const rangeEnd = Math.min(skip + limit, totalItems);

  return (
    <div className="w-100 d-flex flex-md-row flex-column align-items-md-center justify-content-between mt-3 gap-2">
      <div className="d-flex flex-row gap-2 align-items-center w-100">
        <span
          className="font-24 cursor-pointer btn-outline-primary btn d-flex align-items-center justify-content-center"
          style={{ width: "34px", height: "34px", borderRadius: "4px" }}
          onClick={() => goToPage(1)}
        >
          <i className="fa-solid fa-angles-left" style={{ fontSize: 16 }} />
        </span>

        <span
          className="font-24 cursor-pointer btn-outline-primary btn d-flex align-items-center justify-content-center"
          style={{ width: "34px", height: "34px", borderRadius: "4px" }}
          onClick={() => goToPage(Math.max(currentPage - 1, 1))}
        >
          <i className="fa-solid fa-angle-left" style={{ fontSize: 16 }} />
        </span>

        <span className="mx-2 flex-row justify-content-center flex-md-grow-0 flex-grow-1">
          {rangeStart} - {rangeEnd} &nbsp;{t("TABLE.OF")}&nbsp; {totalItems}
        </span>

        <span
          className="font-24 cursor-pointer btn-outline-primary btn d-flex align-items-center justify-content-center"
          style={{ width: "34px", height: "34px", borderRadius: "4px" }}
          onClick={() => goToPage(Math.min(currentPage + 1, numberOfPages))}
        >
          <i className="fa-solid fa-angle-right" style={{ fontSize: 16 }} />
        </span>

        <span
          className="font-24 cursor-pointer btn-outline-primary btn d-flex align-items-center justify-content-center"
          style={{ width: "34px", height: "34px", borderRadius: "4px" }}
          onClick={() => goToPage(numberOfPages)}
        >
          <i className="fa-solid fa-angles-right" style={{ fontSize: 16 }} />
        </span>
      </div>

      <div className="d-flex align-items-center gap-2">
        <span style={{ whiteSpace: "nowrap" }}>{t("TABLE.REQUESTS_PER_PAGE")}</span>
        <select
          className="form-select"
          style={{ minWidth: "80px" }}
          value={limit}
          onChange={onItemsPerPageChange}
        >
          {itemsPerPageOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};
